/**
 * Os filtros da pesquisa avançada de uma fonte.
 *
 * Não conhece campo nenhum. Recebe o catálogo da API — que sai do formulário
 * da própria fonte — e desenha o controle pelo `tipo` de cada um, agrupado
 * pelo `grupo` que vem junto. É o mesmo desenho auto-descritivo da tela de
 * Configuração, e pela mesma razão: os ids das áreas do Licitmais são do
 * perfil da conta e mudam com o contrato, então qualquer lista escrita aqui
 * viraria filtro silenciosamente errado.
 *
 * As chaves do estado são o nome do campo NA FONTE (`idestado[]`, colchetes e
 * tudo). Um mapa de apelido para campo real seria mais uma cópia para divergir.
 */
import { useState, type CSSProperties } from "react";
import type { Filtro } from "../api/cliente";
import css from "./FiltrosDaFonte.module.css";

/** O que a tela preencheu: campo da fonte -> valor, ou lista de valores. */
export type ValoresFiltro = Record<string, string | string[]>;

function alterna(atual: string[], valor: string) {
  return atual.includes(valor)
    ? atual.filter((v) => v !== valor)
    : [...atual, valor];
}

/** O rótulo mais comprido do grupo, que decide a largura da coluna. */
function maiorRotulo(opcoes: { rotulo: string }[]) {
  return opcoes.reduce((m, o) => Math.max(m, o.rotulo.length), 0);
}

/** O bloco precisa da seção inteira? Muitas opções, ou rótulos longos. */
function precisaDeLargura(f: Filtro) {
  const opcoes = f.opcoes ?? [];
  return opcoes.length > 6 || maiorRotulo(opcoes) > 20;
}

/** Preenchido de verdade — string vazia e lista vazia não contam. */
function ativo(v: string | string[] | undefined) {
  return Array.isArray(v) ? v.length > 0 : !!v;
}

/**
 * Grupos na ordem em que a fonte os mandou.
 *
 * `Map`, e não objeto indexado: com `noUncheckedIndexedAccess` cada acesso a
 * `obj[chave]` volta como possivelmente indefinido e o código enche de `!`.
 * O Map preserva a ordem de inserção, que é a ordem do formulário deles.
 */
function agrupa(filtros: Filtro[]) {
  const por = new Map<string, Filtro[]>();
  for (const f of filtros) {
    const atual = por.get(f.grupo);
    if (atual) atual.push(f);
    else por.set(f.grupo, [f]);
  }
  return [...por].map(([nome, campos]) => ({ nome, campos }));
}

function Controle({
  f,
  valor,
  mudou,
  desabilitado,
}: {
  f: Filtro;
  valor: string | string[] | undefined;
  mudou: (v: string | string[]) => void;
  desabilitado: boolean;
}) {
  const lista = Array.isArray(valor) ? valor : [];
  // `opcoes` chega opcional do esquema gerado — no Pydantic ela tem default.
  // Normalizar aqui, uma vez, evita `?? []` repetido em cada uso abaixo.
  const opcoes = f.opcoes ?? [];

  if (f.tipo === "select") {
    return (
      <select
        className={css.campo}
        value={typeof valor === "string" ? valor : ""}
        onChange={(e) => mudou(e.currentTarget.value)}
        disabled={desabilitado}
      >
        {/* A opção vazia é nossa, não do formulário deles: o parser descarta
            `value=""` para que "sem filtro" seja ausência, e não um valor que
            precise ser reconhecido lá na montagem do pedido. */}
        <option value="">Qualquer</option>
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.rotulo}
          </option>
        ))}
      </select>
    );
  }

  if (f.tipo === "checkbox" || f.tipo === "radio") {
    const multiplo = f.multiplo;
    const todos = multiplo && opcoes.length > 1 && lista.length === opcoes.length;
    return (
      <>
        {/* O portal tem "Todos/Todas" em cada grupo — lá é só JavaScript, não
            um valor que se envie. Aqui é o mesmo atalho: sem ele, marcar as 27
            UFs são 27 cliques, e o filtro deixa de ser usável justamente onde
            mais economizaria rede. */}
        {multiplo && opcoes.length > 1 && (
          <label className={`${css.opcao} ${css.todos}`}>
            <input
              type="checkbox"
              checked={todos}
              // Indeterminado quando há alguns marcados: sem isso a caixa fica
              // vazia com metade do grupo selecionado, e parece que nada está.
              ref={(el) => {
                if (el) el.indeterminate = lista.length > 0 && !todos;
              }}
              onChange={() => mudou(todos ? [] : opcoes.map((o) => o.valor))}
              disabled={desabilitado}
            />
            <span>Todos</span>
            <b className={css.conta}>
              {lista.length}/{opcoes.length}
            </b>
          </label>
        )}
        {/* A largura da coluna sai do rótulo mais longo do grupo, em `ch` —
            não de faixas fixas. Duas faixas erravam nas pontas: as 27 UFs
            ("Rio Grande do Norte") caíam na larga e desperdiçavam metade do
            espaço, enquanto as 14 áreas ("Consultoria em Educação e Ensino",
            60 caracteres) não cabiam nem na larga. Com o teto de 34ch, rótulo
            comprido quebra em duas linhas em vez de exigir coluna gigante. */}
        <div
          className={opcoes.length > 6 ? css.grade : css.linha}
          style={
            opcoes.length > 6
              ? ({
                  "--col": `${Math.min(maiorRotulo(opcoes), 34)}ch`,
                } as CSSProperties)
              : undefined
          }
        >
          {opcoes.map((o) => (
            <label key={o.valor} className={css.opcao}>
              <input
                type={multiplo ? "checkbox" : "radio"}
                name={f.nome}
                checked={multiplo ? lista.includes(o.valor) : valor === o.valor}
                onChange={() => mudou(multiplo ? alterna(lista, o.valor) : o.valor)}
                disabled={desabilitado}
              />
              <span title={o.rotulo}>{o.rotulo}</span>
            </label>
          ))}
        </div>
      </>
    );
  }

  return (
    <input
      className={css.campo}
      // `date` nativo mostra e devolve no formato do navegador; a fonte quer
      // dd/mm/aaaa. A conversão fica no servidor, num lugar só — aqui o campo
      // guarda o que a pessoa digitou.
      type="text"
      inputMode={f.tipo === "data" ? "numeric" : undefined}
      placeholder={f.dica ?? ""}
      value={typeof valor === "string" ? valor : ""}
      onChange={(e) => mudou(e.currentTarget.value)}
      disabled={desabilitado}
    />
  );
}

export function FiltrosDaFonte({
  filtros,
  valores,
  mudou,
  desabilitado = false,
}: {
  filtros: Filtro[];
  valores: ValoresFiltro;
  mudou: (v: ValoresFiltro) => void;
  desabilitado?: boolean;
}) {
  const [abertos, setAbertos] = useState(false);

  if (!filtros.length) return null;

  const principais = agrupa(filtros.filter((f) => f.principal));
  const opcionais = agrupa(filtros.filter((f) => !f.principal));

  function define(nome: string, v: string | string[]) {
    const proximo = { ...valores };
    // Vazio é AUSÊNCIA, não string vazia: quem monta o pedido decide o que
    // mandar, e um `""` no meio do caminho faria o campo parecer preenchido.
    if (v === "" || (Array.isArray(v) && v.length === 0)) delete proximo[nome];
    else proximo[nome] = v;
    mudou(proximo);
  }

  const usados = filtros.filter((f) => ativo(valores[f.nome])).length;
  const usadosOcultos = opcionais
    .flatMap((g) => g.campos)
    .filter((f) => ativo(valores[f.nome])).length;

  /** Um grupo: cabeçalho numerado, e os campos dele. */
  function Secao({
    n,
    nome,
    campos,
  }: {
    n: number;
    nome: string;
    campos: Filtro[];
  }) {
    return (
      <section className={css.secao}>
        <h3 className={css.cabecalho}>
          <span className={css.n}>{n}</span>
          {nome}
        </h3>
        <div className={css.campos}>
          {campos.map((f) => (
            <div
              key={f.nome}
              // Toma a linha inteira quando o conteúdo pede: muitas opções
              // (as 27 UFs) ou rótulos longos (as áreas, e "CPE - COMPRA
              // ELETRÔNICA" com só cinco opções). Espremido numa coluna de
              // 230px o bloco não tem largura para rótulo nenhum, e era daí
              // que vinha o truncamento — não do texto ser longo, mas de
              // dividir espaço com vizinhos que precisam de muito menos.
              className={[
                css.bloco,
                precisaDeLargura(f) ? css.largo : "",
                ativo(valores[f.nome]) ? css.preenchido : "",
              ].join(" ")}
            >
              <span className={css.rotulo}>{f.rotulo}</span>
              <Controle
                f={f}
                valor={valores[f.nome]}
                mudou={(v) => define(f.nome, v)}
                desabilitado={desabilitado}
              />
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <div className={css.tudo}>
      <header className={css.topo}>
        <span className={css.titulo}>Filtros da fonte</span>
        {usados > 0 && (
          <>
            <span className={css.resumo}>
              {usados} {usados === 1 ? "campo preenchido" : "campos preenchidos"}
            </span>
            <button
              type="button"
              className={css.limpar}
              onClick={() => mudou({})}
              disabled={desabilitado}
            >
              Limpar
            </button>
          </>
        )}
      </header>

      {principais.map((g, i) => (
        <Secao key={g.nome} n={i + 1} nome={g.nome} campos={g.campos} />
      ))}

      <button
        type="button"
        className={css.mais}
        onClick={() => setAbertos((a) => !a)}
        aria-expanded={abertos}
      >
        {abertos ? "Menos filtros" : "Mais filtros"}
        {/* O contador é o que impede um filtro esquecido de sumir de vista:
            fechado, o painel esconde o que ainda está restringindo a busca. */}
        {!abertos && usadosOcultos > 0 && <b>{usadosOcultos}</b>}
      </button>

      {abertos &&
        opcionais.map((g, i) => (
          <Secao
            key={g.nome}
            n={principais.length + i + 1}
            nome={g.nome}
            campos={g.campos}
          />
        ))}
    </div>
  );
}
